import { Dropdown, TextInput } from 'components/common'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { PosConfigFormValues } from 'types/configuration'

const BagConfiguration = () => {
  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<PosConfigFormValues>()

  const selectedBagSize = watch('selectedBagSize')

  const bags = watch('mobile.bags') || []

  const [imagePreview, setImagePreview] = useState<string | null>(null)

  // When selectedBagSize changes, populate the barcode and price fields
  useEffect(() => {
    if (!selectedBagSize) return

    const selectedBag = bags.find((bag) => bag.size === selectedBagSize)
    if (!selectedBag) return

    const { barcode, price, description, img } = selectedBag

    setValue(`bagBarcodes.${selectedBagSize}`, barcode || '')
    setValue(`bagPrices.${selectedBagSize}`, price?.toString() || '')
    setValue(`bagDescriptions.${selectedBagSize}`, description || '')
    setValue(`bagImages.${selectedBagSize}`, img || '')

    setImagePreview(img || null)
  }, [selectedBagSize, bags, setValue])

  const bagOptions = useMemo(() => {
    // Only add --NONE-- if no actual bags exist
    if (bags.length === 0) {
      return [{ label: '--NONE--', value: '--NONE--', disabled: false }]
    }

    // Add the --NONE-- option, then map the actual bags
    return [
      ...bags.map((bag) => ({
        label: bag.size,
        value: bag.size,
      })),
    ]
  }, [bags])

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value
    setImagePreview(url)
    if (selectedBagSize) {
      setValue(`bagImages.${selectedBagSize}`, url)
    }
  }

  const RenderField = ({
    name,
    label,
  }: {
    name: keyof PosConfigFormValues | string
    label: string
  }) => (
    <Controller
      name={name as any}
      control={control}
      render={({ field }) => (
        <TextInput {...field} label={label} value={field?.value || ''} />
      )}
    />
  )

  return (
    <div className="mt-6 rounded-lg border border-gray-300 bg-gray-50 p-4">
      <h3 className="mb-4 font-semibold">Bag Configuration</h3>

      <div className="flex flex-col gap-10 md:flex-row">
        <div className="flex max-w-lg flex-1 flex-col gap-4">
          <Controller
            name="selectedBagSize"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                setValue={setValue}
                options={bagOptions}
                label="Bag Size"
                error={errors?.selectedBagSize?.message as string}
              />
            )}
          />

          {selectedBagSize && selectedBagSize !== '--NONE--' && (
            <>
              <RenderField
                name={`bagBarcodes.${selectedBagSize}`}
                label="Barcode"
              />

              <RenderField
                name={`bagDescriptions.${selectedBagSize}`}
                label="Description"
              />

              <RenderField
                name={`bagPrices.${selectedBagSize}`}
                label="Price"
              />
            </>
          )}
        </div>

        {/* Right Column → Image URL + Preview */}
        {selectedBagSize && selectedBagSize !== '--NONE--' && (
          <div className="flex w-full max-w-sm flex-col gap-4">
            <Controller
              key={`image-${selectedBagSize}`}
              name={`bagImages.${selectedBagSize}`}
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Image URL"
                  placeholder="Enter image URL"
                  onChange={(e) => {
                    field.onChange(e)
                    handleImageUrlChange(e)
                  }}
                  value={field.value || ''}
                />
              )}
            />

            <div className="flex flex-col">
              <label className="mb-2 text-sm font-medium text-gray-800">
                Image Preview
              </label>
              <div className="flex size-32 items-center justify-center rounded-lg border bg-white shadow-sm">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={`${selectedBagSize} bag`}
                    className="max-h-full max-w-full object-contain"
                    onError={() => setImagePreview(null)}
                  />
                ) : (
                  <span className="px-2 text-center text-sm text-gray-400">
                    No image available
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BagConfiguration
